import { generateClient } from 'aws-amplify/api';

const client = generateClient();

// Convert signed URLs to public URLs - same logic as in ImageUpload component
const getPublicImageUrl = (url) => {
  // Handle null or undefined
  if (!url) {
    return '/placeholder-product.svg';
  }
  
  // Convert URL object to string if needed
  let urlString;
  if (typeof url === 'string') {
    urlString = url;
  } else if (url instanceof URL) {
    urlString = url.toString();
  } else if (typeof url === 'object' && url.href) {
    urlString = url.href;
  } else {
    console.warn('getPublicImageUrl received unsupported value:', url, typeof url);
    return '/placeholder-product.svg';
  }
  
  const bucketName = 'vidhifarm-storage-219dfeef04fa1-main';
  const region = 'ap-south-1';
  
  if (urlString.includes(`${bucketName}.s3.${region}.amazonaws.com/`)) {
    const urlParts = urlString.split(`${bucketName}.s3.${region}.amazonaws.com/`)[1];
    const filePath = urlParts ? urlParts.split('?')[0] : '';
    return `https://${bucketName}.s3.${region}.amazonaws.com/${filePath}`;
  }
  
  return urlString;
};

// GraphQL operations for featured products
const listFeaturedProductsForLanding = /* GraphQL */ `
  query ListFeaturedProductsForLanding(
    $filter: ModelFeaturedProductFilterInput
    $limit: Int
    $nextToken: String
  ) {
    listFeaturedProducts(filter: $filter, limit: $limit, nextToken: $nextToken) {
      items {
        id
        productId
        displayOrder
        isActive
        startDate
        endDate
        product {
          id
          name
          description
          shortDescription
          price
          discountPrice
          stock
          isActive
          tags
          farmingMethod
          images {
            items {
              id
              url
              altText
              isPrimary
              order
            }
          }
          category {
            id
            name
          }
        }
        createdAt
        updatedAt
      }
      nextToken
    }
  }
`;

export class FeaturedProductService {
  /**
   * Get featured products for the landing page
   * Returns active featured products sorted by display order
   */
  static async getFeaturedProductsForLanding() {
    try {
      const today = new Date().toISOString().split('T')[0]; // Get today's date in YYYY-MM-DD format
      
      const result = await client.graphql({
        query: listFeaturedProductsForLanding,
        variables: {
          filter: {
            isActive: { eq: true },
            // You can add date filtering if needed
            // startDate: { le: today },
            // endDate: { ge: today }
          },
          limit: 6, // Maximum 6 featured products
        }
      });
      
      const featuredProducts = result.data.listFeaturedProducts.items || [];
      
      // Filter out products that are not active or out of stock
      const validFeaturedProducts = featuredProducts.filter(fp => {
        const product = fp.product;
        if (!product || !product.isActive || product.stock <= 0) {
          return false;
        }
        
        // Check date range if specified
        if (fp.startDate && today < fp.startDate) {
          return false;
        }
        
        if (fp.endDate && today > fp.endDate) {
          return false;
        }
        
        return true;
      });
      
      // Sort by display order
      validFeaturedProducts.sort((a, b) => a.displayOrder - b.displayOrder);
      
      // Transform to the format expected by the HomePage component
      return validFeaturedProducts.map(fp => {
        const product = fp.product;
        const primaryImage = product.images?.items?.find(img => img.isPrimary);
        const fallbackImage = product.images?.items?.[0];
        
        return {
          id: product.id,
          name: product.name,
          description: product.description,
          shortDescription: product.shortDescription,
          price: product.discountPrice || product.price,
          originalPrice: product.discountPrice ? product.price : null,
          unit: 'kg', // You might want to add this to your Product model
          image: getPublicImageUrl(primaryImage?.url || fallbackImage?.url) || '/placeholder-product.svg',
          rating: 4.5, // You might want to calculate this from reviews
          reviews: 0, // You might want to count this from reviews
          isOrganic: product.farmingMethod === 'Organic',
          inStock: product.stock > 0,
          stock: product.stock,
          tags: product.tags || [],
          category: product.category?.name || '',
          featuredOrder: fp.displayOrder,
        };
      });
      
    } catch (error) {
      console.error('Error fetching featured products for landing:', error);
      throw new Error('Failed to load featured products');
    }
  }
  
  /**
   * Check if a product is currently featured
   */
  static async isProductFeatured(productId) {
    try {
      const result = await client.graphql({
        query: listFeaturedProductsForLanding,
        variables: {
          filter: {
            productId: { eq: productId },
            isActive: { eq: true },
          },
          limit: 1,
        }
      });
      
      return result.data.listFeaturedProducts.items.length > 0;
    } catch (error) {
      console.error('Error checking if product is featured:', error);
      return false;
    }
  }
  
  /**
   * Get available display orders (1-6 that are not taken)
   */
  static async getAvailableDisplayOrders() {
    try {
      const result = await client.graphql({
        query: listFeaturedProductsForLanding,
        variables: {
          filter: {
            isActive: { eq: true },
          },
          limit: 6,
        }
      });
      
      const usedOrders = result.data.listFeaturedProducts.items.map(fp => fp.displayOrder);
      const allOrders = [1, 2, 3, 4, 5, 6];
      
      return allOrders.filter(order => !usedOrders.includes(order));
    } catch (error) {
      console.error('Error getting available display orders:', error);
      return [1, 2, 3, 4, 5, 6];
    }
  }
}